/**
 * Generic gtag auto-tracking — load once per page (site uses GA4 gtag.js via
 * the Shopify "Google & YouTube" channel, not Google Tag Manager, so events
 * are sent straight to window.gtag() — same as the sendGtagEvent() helper in
 * _js/core/analytics/google.ts).
 *
 * Every event carries a fixed param set: send_to, page_url, target_url,
 * index, funnel_stage, module, event_label, event_value, item_id,
 * option_index, item_name.
 *
 * Mark CTAs with .track-cta + data-track-* attributes or track-* classes.
 * Clicks auto-fire gtag('event', ...) calls. Configure via window.DlTrackConfig before load.
 *
 * `module` is the section name: add data-track-module="section_name" on a
 * section's root element and every tracked element inside it (clicks, views)
 * auto-picks it up via closest(). `funnel_stage` is the page-type label
 * (home, product_page, cart_page, ...), set once per page in theme.liquid
 * via window.DlTrackConfig.funnelStage.
 *
 * Quick start:
 *   <script>window.DlTrackConfig = { defaultCurrency: "USD" };</script>
 *   <script src="datalayer-track.js"></script>
 *
 *   <section data-track-module="pricing">
 *     <a class="track-cta track-action-cta_click" href="#pricing">Get started</a>
 *     <button class="track-cta track-action-checkout
 *                    track-id-pro_01 track-name-Pro_Plan track-value-49 track-currency-USD">
 *       Buy now
 *     </button>
 *   </section>
 */
(function () {
  "use strict";

  if (window.DlTrack && window.DlTrack.__initialized) return;

  var DEFAULT_CONFIG = {
    defaultCurrency: "USD",
    defaultAction: "cta_click",
    classPrefix: "track",
    ctaSelector: ".track-cta,[data-track-cta]",
    logoSelector: ".track-logo",
    infoSelector: ".track-info",
    tooltipSelector: ".track-tooltip",
    checkoutRoot: null,
    productCatalog: {},
    moduleSelector: "[data-track-module]",
    events: {
      ctaClick: "click_custom",
      addToCart: "add_to_cart",
      beginCheckout: "begin_checkout",
      purchaseComplete: "purchase_complete",
      logoClick: "click_custom",
      infoClick: "click_custom",
      tooltipClick: "tooltip_click",
      elementView: "impression_custom",
    },
    checkoutActions: ["checkout", "buy", "purchase"],
    watchPurchaseEvent: true,
    purchaseSourceEvent: "purchase",
  };

  var CONFIG = Object.assign(
    {},
    DEFAULT_CONFIG,
    window.DlTrackConfig || {}
  );

  if (window.DlTrackConfig && window.DlTrackConfig.events) {
    CONFIG.events = Object.assign({}, DEFAULT_CONFIG.events, window.DlTrackConfig.events);
  }

  window.dataLayer = window.dataLayer || [];

  var PREFIX = CONFIG.classPrefix;
  var VIEWED_ONCE = {};

  // GA4 measurement ID, read once from the analytics-google section's
  // settings JSON so every event can carry it as `send_to`.
  var ANALYTICS_CONFIG = (function () {
    try {
      var el = document.getElementById("analytics-google-config");
      if (!el) return {};
      return JSON.parse(el.textContent || el.innerText || "{}") || {};
    } catch (_) {
      return {};
    }
  })();
  var MEASUREMENT_ID = ANALYTICS_CONFIG.gtag || "";

  // Configure gtag for this destination ourselves, right now, instead of
  // relying on the analytics-google section's own behavior script (loaded
  // later, via the main JS bundle) to have already called gtag('config', ...)
  // by the time an event needs it. An `event` sent to a `send_to` destination
  // gtag hasn't been configured for yet is silently dropped — no network
  // request is ever dispatched for it.
  if (
    ANALYTICS_CONFIG.enabled !== false &&
    MEASUREMENT_ID &&
    !window.__dlGtagConfigured
  ) {
    window.__dlGtagConfigured = true;
    // gtag may not be defined yet this early — queue the config command
    // directly on dataLayer (same fallback sendEvent() uses) so it lands
    // ahead of any event our own IntersectionObserver fires later, instead
    // of being silently skipped for lacking a configured destination.
    if (window.gtag) {
      window.gtag("config", MEASUREMENT_ID, { send_page_view: false });
    } else {
      window.dataLayer.push(["config", MEASUREMENT_ID, { send_page_view: false }]);
    }
  }

  var FIELD_DEFAULTS = {
    send_to: "",
    page_url: "",
    target_url: "",
    index: "",
    funnel_stage: "",
    module: "",
    event_label: "",
    event_value: "",
    item_id: "",
    option_index: "",
    item_name: "",
  };

  // gtag drops events sent before the page's cookie-consent default is set.
  // Queue events as they happen; flush them once the page has loaded.
  var GTAG_READY = document.readyState === "complete";
  var EVENT_QUEUE = [];

  function emitEvent(eventName, data) {
    if (window.gtag) {
      window.gtag("event", eventName, data);
    } else if (window.dataLayer && window.dataLayer.push) {
      window.dataLayer.push(["event", eventName, data]);
    }
  }

  function flushEventQueue() {
    GTAG_READY = true;
    var queued = EVENT_QUEUE;
    EVENT_QUEUE = [];
    queued.forEach(function (item) {
      emitEvent(item.eventName, item.data);
    });
  }

  if (!GTAG_READY) {
    window.addEventListener("load", flushEventQueue);
  }

  // Sends straight through window.gtag() (matches sendGtagEvent() in
  // _js/core/analytics/google.ts). Falls back to a raw dataLayer.push of the
  // gtag argument array if gtag hasn't loaded yet, same as that helper does.
  function sendEvent(eventName, fields) {
    try {
      var base = {
        page_url: window.location.pathname || "",
        funnel_stage:
          (window.DlTrackConfig && window.DlTrackConfig.funnelStage) || "",
      };
      // Only attach send_to when we have a real measurement ID. An empty
      // send_to routes to nowhere; omitting it lets gtag use the default
      // configured GA4 property (set up by Shopify's Google & YouTube channel).
      if (MEASUREMENT_ID) {
        base.send_to = MEASUREMENT_ID;
      }

      var data = Object.assign({}, FIELD_DEFAULTS, base, fields || {});
      if (!MEASUREMENT_ID) {
        delete data.send_to;
      }

      if (!GTAG_READY) {
        EVENT_QUEUE.push({ eventName: eventName, data: data });
        return;
      }

      emitEvent(eventName, data);
    } catch (error) {
      console.warn("[datalayer-track] Failed to send event", eventName, fields, error);
    }
  }

  // Back-compat wrapper for the public DlTrack.push API (raw payload with an
  // `event` key), used internally in a couple of spots too.
  function pushLayer(payload) {
    payload = payload || {};
    var fields = Object.assign({}, payload);
    var eventName = fields.event;
    delete fields.event;
    sendEvent(eventName, fields);
  }

  function pushEvent(eventName, data) {
    sendEvent(eventName, data);
  }

  function textTrim(el) {
    if (!el) return "";
    var inner = el.querySelector && el.querySelector("[class*='__label']");
    var raw = inner ? inner.textContent : el.textContent;
    return (raw || "").replace(/\s+/g, " ").trim().slice(0, 40);
  }

  function getValueFromClass(el, key) {
    if (!el || !el.className || typeof el.className !== "string") return "";
    var regex = new RegExp(
      "(?:^|\\s)" + PREFIX + "-" + key + "-([a-zA-Z0-9_.-]+)(?:\\s|$)"
    );
    var match = el.className.match(regex);
    return match ? match[1].trim() : "";
  }

  function readAttr(el, key) {
    return (el.getAttribute("data-track-" + key) || "").trim();
  }

  function getAction(el) {
    var action = readAttr(el, "action").toLowerCase();
    if (action) return action;

    var classAction = getValueFromClass(el, "action");
    if (classAction) return classAction.toLowerCase();

    if (el.hasAttribute("data-track-cta") && readAttr(el, "cta-type") === "join") {
      return "join";
    }
    return "";
  }

  // `module` is the section name. Override per-element with data-track-module,
  // else look up the closest ancestor carrying data-track-module="section_name".
  function detectModule(el) {
    var explicit = readAttr(el, "module");
    if (explicit) return explicit;

    if (!el.closest) return "page";

    var moduleEl = el.closest(CONFIG.moduleSelector);
    return moduleEl ? moduleEl.getAttribute("data-track-module") || "page" : "page";
  }

  // Position of `el` among trackable elements in its module container (or the
  // whole document if none). Override per-element with data-track-index.
  function getIndex(el) {
    var explicit = readAttr(el, "index");
    if (explicit) return explicit;
    if (!el.closest) return "";

    var container = el.closest(CONFIG.moduleSelector) || document;

    var trackable = container.querySelectorAll(
      [CONFIG.ctaSelector, CONFIG.logoSelector, CONFIG.infoSelector].join(",")
    );
    var idx = Array.prototype.indexOf.call(trackable, el);
    return idx >= 0 ? String(idx) : "";
  }

  function readProduct(el) {
    var productId =
      readAttr(el, "product-id") ||
      readAttr(el, "plan-id") ||
      getValueFromClass(el, "id");

    var name = readAttr(el, "product-name");
    if (!name) {
      var className = getValueFromClass(el, "name");
      if (className) name = className.replace(/_/g, " ");
    }
    if (!name && productId && CONFIG.productCatalog[productId]) {
      name = CONFIG.productCatalog[productId];
    }

    var valueRaw = readAttr(el, "value") || getValueFromClass(el, "value");
    var value = valueRaw ? parseFloat(valueRaw) : 0;

    var currency = (
      readAttr(el, "currency") ||
      getValueFromClass(el, "currency") ||
      CONFIG.defaultCurrency
    ).toUpperCase();

    var qtyRaw = readAttr(el, "quantity") || getValueFromClass(el, "qty");
    var quantity = qtyRaw ? parseInt(qtyRaw, 10) : 1;
    if (!quantity || quantity < 1) quantity = 1;

    return {
      id: productId,
      name: name,
      value: isNaN(value) ? 0 : value,
      currency: currency,
      quantity: quantity,
    };
  }

  function buildEcommerce(product) {
    return {
      value: product.value,
      currency: product.currency,
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          price: product.value,
          quantity: product.quantity,
        },
      ],
    };
  }

  function isCheckoutAction(action) {
    return CONFIG.checkoutActions.indexOf(action) !== -1;
  }

  function pushCtaClick(el, eventValue, moduleName, extra) {
    var fields = Object.assign(
      {
        target_url: el.getAttribute("href") || el.getAttribute("data-href") || "",
        index: getIndex(el),
        module: moduleName,
        event_label: readAttr(el, "label") || textTrim(el),
        event_value: eventValue,
        option_index: readAttr(el, "option-index") || "",
      },
      extra || {}
    );

    pushEvent(CONFIG.events.ctaClick, fields);

    var customEvent = readAttr(el, "event");
    if (customEvent) {
      pushEvent(customEvent, fields);
    }
  }

  function pushAddToCart(product, zone, label, extra, el) {
    pushEvent(
      CONFIG.events.addToCart,
      Object.assign(
        {
          event_value: "add_to_cart",
          module: zone,
          event_label: label,
          target_url: el ? el.getAttribute("href") || "" : "",
          index: el ? getIndex(el) : "",
          item_id: product.id,
          item_name: product.name,
          value: product.value,
          currency: product.currency,
          quantity: product.quantity,
          ecommerce: buildEcommerce(product),
        },
        extra || {}
      )
    );
  }

  function pushBeginCheckout(product, zone, label, extra, el) {
    pushEvent(
      CONFIG.events.beginCheckout,
      Object.assign(
        {
          event_value: "checkout",
          module: zone,
          event_label: label,
          target_url: el ? el.getAttribute("href") || "" : "",
          index: el ? getIndex(el) : "",
          item_id: product.id,
          item_name: product.name,
          value: product.value,
          currency: product.currency,
          quantity: product.quantity,
          ecommerce: buildEcommerce(product),
        },
        extra || {}
      )
    );
  }

  function trackCheckoutClick(el, extra) {
    if (!el) return;
    var product = readProduct(el);
    if (!product.name) {
      console.warn("[datalayer-track] Missing product name for checkout CTA");
      return;
    }
    var moduleName = detectModule(el);
    var label = textTrim(el);
    var meta = Object.assign(
      {
        item_id: product.id,
        item_name: product.name,
        value: product.value,
        currency: product.currency,
        quantity: product.quantity,
      },
      extra || {}
    );
    pushCtaClick(el, "checkout", moduleName, meta);
    pushAddToCart(product, moduleName, label, extra, el);
    pushBeginCheckout(product, moduleName, label, extra, el);
  }

  function viewOnce(key, fn) {
    if (VIEWED_ONCE[key]) return;
    VIEWED_ONCE[key] = true;
    fn();
  }

  // The GA4 event NAME fired is always CONFIG.events.elementView
  // ("impression_custom"); `viewName` (e.g. "star_rating_view") is carried
  // in event_value instead, same pattern as click_custom/event_value.
  function observeInView(selector, viewName, extra, threshold) {
    if (!window.IntersectionObserver) return;
    var nodes = document.querySelectorAll(selector);
    if (!nodes.length) return;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var target = entry.target;
          var id =
            target.id || target.getAttribute("data-track-id") || selector;
          viewOnce(viewName + ":" + id, function () {
            pushEvent(
              CONFIG.events.elementView,
              Object.assign(
                {
                  event_value: viewName,
                  module: detectModule(target),
                  event_label: readAttr(target, "label") || textTrim(target) || viewName,
                  target_url: target.getAttribute("href") || "",
                  index: getIndex(target),
                },
                extra || {}
              )
            );
          });
          io.unobserve(entry.target);
        });
      },
      { threshold: threshold == null ? 1 : threshold }
    );

    nodes.forEach(function (node) {
      io.observe(node);
    });
  }

  function initViewTracking() {
    var views = CONFIG.viewSelectors || [];
    views.forEach(function (item) {
      if (!item || !item.selector || !item.event) return;
      observeInView(item.selector, item.event, item.data || {}, item.threshold);
    });

    document.querySelectorAll("[data-track-view]").forEach(function (el) {
      var viewName = el.getAttribute("data-track-view");
      if (!viewName) return;

      var minWidth = readAttr(el, "view-min-width");
      if (minWidth && window.innerWidth < parseInt(minWidth, 10)) return;

      var thresholdAttr = readAttr(el, "view-threshold");
      var threshold = thresholdAttr ? parseFloat(thresholdAttr) : undefined;

      var selector =
        el.id ? "#" + el.id : "[data-track-view='" + viewName + "']";
      observeInView(
        selector,
        viewName,
        {
          item_id: el.id || "",
          item_name: el.getAttribute("data-track-name") || "",
        },
        threshold
      );
    });
  }

  // Auto-fires on the native play/ended events of a <video>, for markup that
  // can't carry data-* attributes directly (e.g. Shopify's video_tag filter
  // drops unrecognized params). Put data-track-video-play/-complete plus
  // data-track-video-target="<video selector>" on any plain element near the
  // video (its module container is used as the lookup scope).
  function initVideoTracking() {
    document
      .querySelectorAll("[data-track-video-play],[data-track-video-complete]")
      .forEach(function (el) {
        var targetSelector = readAttr(el, "video-target");
        if (!targetSelector) return;

        var scope = (el.closest && el.closest(CONFIG.moduleSelector)) || document;
        var video = scope.querySelector(targetSelector);
        if (!video || !window.HTMLMediaElement || !(video instanceof HTMLMediaElement)) return;

        var playEvent = readAttr(el, "video-play");
        var completeEvent = readAttr(el, "video-complete");
        var label = readAttr(el, "label") || textTrim(el) || "video";
        var maxWidth = readAttr(el, "video-max-width");

        if (playEvent) {
          video.addEventListener("play", function () {
            if (maxWidth && window.innerWidth > parseInt(maxWidth, 10)) return;
            pushEvent(CONFIG.events.ctaClick, {
              event_value: playEvent,
              module: detectModule(video),
              event_label: label,
              index: getIndex(video),
            });
          });
        }

        if (completeEvent) {
          video.addEventListener("ended", function () {
            pushEvent(completeEvent, {
              event_value: completeEvent,
              module: detectModule(video),
              event_label: label,
            });
          });
        }

        var midEvent = readAttr(el, "video-50");
        if (midEvent) {
          var firedMid = false;
          video.addEventListener("timeupdate", function () {
            if (firedMid || !video.duration) return;
            if (video.currentTime / video.duration >= 0.5) {
              firedMid = true;
              pushEvent(midEvent, {
                event_value: midEvent,
                module: detectModule(video),
                event_label: label,
              });
            }
          });
        }
      });
  }

  function pushLogoClick(el) {
    pushEvent(CONFIG.events.logoClick, {
      event_value: "logo_click",
      module: detectModule(el),
      event_label: textTrim(el) || "logo",
      target_url: el.getAttribute("href") || "",
      index: getIndex(el),
    });
  }

  function pushInfoClick(el) {
    pushEvent(CONFIG.events.infoClick, {
      event_value: readAttr(el, "value") || "info_click",
      module: detectModule(el),
      event_label: readAttr(el, "label") || textTrim(el) || "info",
      target_url: el.getAttribute("href") || "",
      index: getIndex(el),
      info_context: readAttr(el, "context") || "",
    });
  }

  function pushTooltipClick(el, interaction) {
    pushEvent(CONFIG.events.tooltipClick, {
      event_value: "tooltip_click",
      module: detectModule(el),
      event_label: textTrim(el) || "tooltip",
      index: getIndex(el),
      interaction: interaction || "click",
    });
  }

  function alreadySentPurchase(orderId) {
    var key = "dlPurchase_" + orderId;
    try {
      if (sessionStorage.getItem(key)) return true;
      sessionStorage.setItem(key, "1");
      return false;
    } catch (_) {
      window.__dlPurchaseSent = window.__dlPurchaseSent || {};
      if (window.__dlPurchaseSent[orderId]) return true;
      window.__dlPurchaseSent[orderId] = true;
      return false;
    }
  }

  function pushPurchaseCompleteFromLayer(layer) {
    if (!layer || layer.event !== CONFIG.purchaseSourceEvent) return;

    var ec = layer.ecommerce;
    if (!ec || !ec.transaction_id) return;

    var orderId = String(ec.transaction_id);
    if (alreadySentPurchase(orderId)) return;

    var items = ec.items || [];
    var firstItem = items[0] || {};

    pushEvent(CONFIG.events.purchaseComplete, {
      event_value: "purchase",
      module: "purchase",
      event_label: firstItem.item_name || firstItem.name || "",
      item_id: firstItem.item_id || firstItem.id || "",
      item_name: firstItem.item_name || firstItem.name || "",
      transaction_id: ec.transaction_id,
      value: ec.value,
      currency: ec.currency,
      num_items: items.length,
      ecommerce: {
        transaction_id: ec.transaction_id,
        value: ec.value,
        currency: ec.currency,
        items: items.map(function (item) {
          return {
            item_id: item.item_id || item.id,
            item_name: item.item_name || item.name,
            price: item.price,
            quantity: item.quantity,
          };
        }),
      },
    });
  }

  function installPurchaseListener() {
    if (!CONFIG.watchPurchaseEvent) return;

    var i;
    for (i = 0; i < window.dataLayer.length; i++) {
      pushPurchaseCompleteFromLayer(window.dataLayer[i]);
    }

    var dl = window.dataLayer;
    var originalPush = dl.push;
    dl.push = function () {
      var result = originalPush.apply(dl, arguments);
      for (i = 0; i < arguments.length; i++) {
        pushPurchaseCompleteFromLayer(arguments[i]);
      }
      return result;
    };
  }

  function boot() {
    initViewTracking();
    initVideoTracking();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener(
    "click",
    function (e) {
      if (!e.target || !e.target.closest) return;

      var logoEl = e.target.closest(CONFIG.logoSelector);
      if (logoEl) {
        pushLogoClick(logoEl);
        return;
      }

      var infoEl = e.target.closest(CONFIG.infoSelector);
      if (infoEl) {
        pushInfoClick(infoEl);
        return;
      }

      var tooltipEl = e.target.closest(CONFIG.tooltipSelector);
      if (tooltipEl) {
        pushTooltipClick(tooltipEl, "click");
        return;
      }

      var el = e.target.closest(CONFIG.ctaSelector);
      if (!el) return;

      var action = getAction(el) || CONFIG.defaultAction;

      if (isCheckoutAction(action)) {
        trackCheckoutClick(el);
      } else {
        pushCtaClick(el, action, detectModule(el), {});
      }
    },
    true
  );

  installPurchaseListener();

  window.DlTrack = {
    __initialized: true,
    config: CONFIG,
    push: pushLayer,
    event: pushEvent,
    trackCtaClick: pushCtaClick,
    trackCheckoutClick: trackCheckoutClick,
    trackAddToCart: function (el, extra) {
      var product = readProduct(el);
      pushAddToCart(product, detectModule(el), textTrim(el), extra, el);
    },
    trackBeginCheckout: function (el, extra) {
      var product = readProduct(el);
      pushBeginCheckout(product, detectModule(el), textTrim(el), extra, el);
    },
    trackLogoClick: pushLogoClick,
    trackInfoClick: pushInfoClick,
    trackTooltipClick: pushTooltipClick,
    observeInView: observeInView,
    readProduct: readProduct,
    detectModule: detectModule,
  };
})();
